import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import {
  painelModalCancelButtonClass,
  painelPermissionsModalScrollClass,
} from '@/components/painel/painel-styles';
import { GsImage } from '@/components/help-center/latest-power-shared';
import { AceNavIcon } from '@/components/help-center/MaskedSvgIcon';
import { useCatalogAces } from '@/components/painel/aces/aces-cms-context';
import { formatAceDisplayName } from '@/components/help-center/aces/ace-shared';
import { MOCK_ACES } from '@/lib/aces-data';

export type InsertAceMentionDialogProps = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onInsert: (bbcode: string) => void;
  dark?: boolean;
};

const MAX_RESULTS = 60;

export function InsertAceMentionDialog({
  open,
  onOpenChange,
  onInsert,
  dark: _dark = true,
}: InsertAceMentionDialogProps) {
  // Modais de BBCode especiais usam sempre o tema escuro do painel.
  const dark = true;
  void _dark;
  const { aces: catalog } = useCatalogAces();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const options = useMemo(() => {
    const source = catalog.length > 0 ? catalog : MOCK_ACES;
    const seen = new Set<string>();
    const list: { slug: string; name: string; smiley: string }[] = [];
    for (const a of source) {
      const slug = a.slug.trim().toLowerCase();
      if (!slug || seen.has(slug)) continue;
      seen.add(slug);
      list.push({
        slug,
        name: formatAceDisplayName(slug) || a.name,
        smiley: a.smileyCode || slug,
      });
    }
    return list.sort((a, b) => a.name.localeCompare(b.name, 'pt'));
  }, [catalog]);

  const trimmedQuery = query.trim().toLowerCase();
  const results = useMemo(() => {
    if (!trimmedQuery) return options.slice(0, MAX_RESULTS);
    return options
      .filter(
        (o) => o.name.toLowerCase().includes(trimmedQuery) || o.slug.includes(trimmedQuery)
      )
      .slice(0, MAX_RESULTS);
  }, [options, trimmedQuery]);

  const manualSlug = query.trim().toLowerCase().replace(/\s+/g, '');
  const hasExact = results.some((r) => r.slug === manualSlug);

  const insertSlug = (slug: string, displayName?: string) => {
    const label = displayName?.trim() || slug;
    onInsert(`[[ace:${label}]]`);
    onOpenChange(false);
  };

  const body = (
    <>
      <div className={cn('space-y-4', dark ? 'px-6 py-5 sm:px-7' : 'px-6 py-5')}>
        <div className="relative">
          <Search
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
              dark ? 'text-slate-500' : 'text-gray-400'
            )}
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar Ace por nome ou slug…"
            autoFocus
            className={cn(dark ? cn(painelInputClass, 'pl-9') : 'h-10 w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 font-inter text-sm')}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              e.preventDefault();
              if (results[0]) insertSlug(results[0].slug, results[0].name);
              else if (manualSlug) insertSlug(manualSlug);
            }}
          />
        </div>

        <div
          className={cn(
            'max-h-[min(42vh,360px)] overflow-y-auto rounded-xl',
            dark
              ? cn('border border-white/10 bg-blue-950/30', painelPermissionsModalScrollClass)
              : 'border border-gray-200 bg-gray-50/50'
          )}
        >
          {results.length === 0 ? (
            <p className={cn('px-4 py-10 text-center text-sm', dark ? 'text-slate-500' : 'text-gray-500')}>
              Nenhum Ace encontrado.
            </p>
          ) : (
            <ul className={cn('divide-y p-1.5', dark ? 'divide-white/[0.06]' : 'divide-gray-100')}>
              {results.map((o) => (
                <li key={o.slug}>
                  <button
                    type="button"
                    onClick={() => insertSlug(o.slug, o.name)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-lg px-2.5 py-2.5 text-left',
                      dark ? 'hover:bg-amber-400/10' : 'hover:bg-amber-50'
                    )}
                  >
                    <GsImage
                      code={o.smiley}
                      className={cn(
                        'flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border',
                        dark ? 'border-amber-500/20 bg-[#1a1206]' : 'border-amber-200 bg-white'
                      )}
                      imgClassName="h-8 w-8 object-contain"
                    />
                    <span className="min-w-0 flex-1">
                      <span className={cn('block truncate font-poppins text-sm font-semibold', dark ? 'text-white' : 'text-gray-900')}>
                        {o.name}
                      </span>
                      <span className={cn('mt-0.5 block truncate font-mono text-[11px]', dark ? 'text-slate-500' : 'text-gray-500')}>
                        [[ace:{o.slug}]]
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {manualSlug && !hasExact ? (
          <button
            type="button"
            onClick={() => insertSlug(manualSlug)}
            className={cn(
              'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3',
              dark ? 'border-amber-400/25 bg-amber-500/10' : 'border-amber-200 bg-amber-50'
            )}
          >
            <span className={cn('text-sm', dark ? 'text-slate-300' : 'text-gray-800')}>
              Inserir Ace personalizado
            </span>
            <code className={cn('font-mono text-xs', dark ? 'text-amber-200' : 'text-amber-800')}>
              [[ace:{manualSlug}]]
            </code>
          </button>
        ) : null}
      </div>
      <div className={cn('flex justify-end border-t px-6 py-4', dark ? 'border-white/10' : 'border-gray-100')}>
        <Button
          type="button"
          variant="ghost"
          className={dark ? painelModalCancelButtonClass : 'rounded-xl'}
          onClick={() => onOpenChange(false)}
        >
          Cancelar
        </Button>
      </div>
    </>
  );

  if (dark) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showCloseButton={false}
          className="max-h-[90vh] max-w-lg overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
        >
          <DialogTitle className="sr-only">Mencionar Ace</DialogTitle>
          <PainelPermissionsDialogShell>
            <div className="border-b border-white/10 bg-amber-950/20 px-6 pb-5 pt-6 sm:px-7">
              <div className="flex items-start gap-4 pr-12">
                <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500/25 to-amber-700/10 ring-1 ring-amber-400/35">
                  <AceNavIcon className="h-7 w-7 text-amber-200" aria-hidden />
                </span>
                <div>
                  <p className="font-inter text-[10px] font-bold uppercase tracking-[0.18em] text-amber-300/55">
                    BBCode especial
                  </p>
                  <h2 className="mt-1 font-poppins text-xl font-bold text-white">Mencionar Ace</h2>
                  <p className="mt-1.5 font-inter text-sm text-slate-400">
                    Insere{' '}
                    <code className="rounded-md bg-white/10 px-1.5 py-0.5 font-mono text-xs text-amber-200/90">
                      [[ace:vaf]]
                    </code>{' '}
                    com tooltip no estilo dos Aces.
                  </p>
                </div>
              </div>
            </div>
            {body}
          </PainelPermissionsDialogShell>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg gap-0 overflow-hidden border-gray-200 bg-white p-0 sm:rounded-2xl">
        <DialogTitle className="sr-only">Mencionar Ace</DialogTitle>
        <div className="border-b border-gray-100 bg-amber-50/60 px-6 pb-5 pt-6">
          <h2 className="font-poppins text-xl font-bold text-gray-900">Mencionar Ace</h2>
        </div>
        {body}
      </DialogContent>
    </Dialog>
  );
}
